import React, { useState, useEffect } from 'react';
import errorLogger from '../utils/ErrorLogger';

/**
 * Enhanced error display component with better user messaging
 * and error categorization
 */
const ErrorDisplay = ({ 
  errors = [], 
  onDismiss, 
  onRetry, 
  context = 'general',
  showDetails = false 
}) => {
  if (!errors || errors.length === 0) {
    return null;
  }

  const handleDismiss = (index) => {
    errorLogger.logUserAction('error-dismissed', {
      context,
      errorIndex: index,
      errorMessage: errors[index]?.message || errors[index]
    });
    
    if (onDismiss) {
      onDismiss(index);
    }
  };

  const handleRetry = () => {
    errorLogger.logUserAction('error-retry-clicked', {
      context,
      errorCount: errors.length
    });
    
    if (onRetry) {
      onRetry();
    }
  };

  const getErrorIcon = (error) => {
    const errorStr = error?.message || error || '';
    
    if (errorStr.includes('network') || errorStr.includes('timeout')) {
      return (
        <svg className="me-2" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
          <path d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm7.5-6.923c-.67.204-1.335.82-1.887 1.855-.143.268-.276.56-.395.872.705.157 1.472.257 2.282.287V1.077zM4.249 3.539c.142-.384.304-.744.481-1.078a6.7 6.7 0 0 1 .597-.933A7.01 7.01 0 0 0 3.051 3.05c.362.184.763.349 1.198.49zM3.509 7.5c.036-1.07.188-2.087.436-3.008a9.124 9.124 0 0 1-1.565-.667A6.964 6.964 0 0 0 1.018 7.5h2.49zm1.4-2.741a12.344 12.344 0 0 0-.4 2.741H7.5V5.091c-.91-.03-1.783-.145-2.591-.332zM8.5 5.09V7.5h2.99a12.342 12.342 0 0 0-.399-2.741c-.808.187-1.681.301-2.591.332zM4.51 8.5c.035.987.176 1.914.399 2.741A13.612 13.612 0 0 1 7.5 10.91V8.5H4.51zm3.99 0v2.409c.91.03 1.783.145 2.591.332.223-.827.364-1.754.4-2.741H8.5zm-3.282 3.696c.12.312.252.604.395.872.552 1.035 1.218 1.65 1.887 1.855V11.91c-.81.03-1.577.13-2.282.287zm.11 2.276a6.696 6.696 0 0 1-.598-.933 8.853 8.853 0 0 1-.481-1.079 8.38 8.38 0 0 0-1.198.49 7.01 7.01 0 0 0 2.276 1.522zm-1.383-2.964A13.36 13.36 0 0 1 3.508 8.5h-2.49a6.963 6.963 0 0 0 1.362 3.675c.47-.258.995-.482 1.565-.667zm6.728 2.964a7.009 7.009 0 0 0 2.275-1.521 8.376 8.376 0 0 0-1.197-.49 8.853 8.853 0 0 1-.481 1.078 6.688 6.688 0 0 1-.597.933zM8.5 11.909v3.014c.67-.204 1.335-.82 1.887-1.855.143-.268.276-.56.395-.872A12.63 12.63 0 0 0 8.5 11.91zm3.555-.401c.57.185 1.095.409 1.565.667A6.963 6.963 0 0 0 14.982 8.5h-2.49a13.36 13.36 0 0 1-.437 3.008zM14.982 7.5a6.963 6.963 0 0 0-1.362-3.675c-.47.258-.995.482-1.565.667.248.92.4 1.938.437 3.008h2.49zM11.27 2.461c.177.334.339.694.482 1.078a8.368 8.368 0 0 0 1.196-.49 7.01 7.01 0 0 0-2.275-1.52c.218.283.418.597.597.932zm-.488 1.343a7.765 7.765 0 0 0-.395-.872C9.835 1.897 9.17 1.282 8.5 1.077V4.09c.81-.03 1.577-.13 2.282-.287z"/>
        </svg>
      );
    }
    
    if (errorStr.includes('PDF') || errorStr.includes('file')) {
      return (
        <svg className="me-2" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
          <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z"/>
          <path d="M4.603 14.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.697 19.697 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a10.954 10.954 0 0 0 .98 1.686 5.753 5.753 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.856.856 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.712 5.712 0 0 1-.911-.95 11.651 11.651 0 0 0-1.997.406 11.307 11.307 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.793.793 0 0 1-.58.029zm1.379-1.901c-.166.076-.32.156-.459.238-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361.01.022.02.036.026.044a.266.266 0 0 0 .035-.012c.137-.056.355-.235.635-.572a8.18 8.18 0 0 0 .45-.606zm1.64-1.33a12.71 12.71 0 0 1 1.01-.193 11.744 11.744 0 0 1-.51-.858 20.801 20.801 0 0 1-.5 1.05zm2.446.45c.15.163.296.3.435.41.24.19.407.253.498.256a.107.107 0 0 0 .07-.015.307.307 0 0 0 .094-.125.436.436 0 0 0 .059-.2.095.095 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a3.876 3.876 0 0 0-.612-.053zM8.078 7.8a6.7 6.7 0 0 0 .2-.828c.031-.188.043-.343.038-.465a.613.613 0 0 0-.032-.198.517.517 0 0 0-.145.04c-.087.035-.158.106-.196.283-.04.192-.03.469.135.968z"/>
        </svg>
      );
    }
    
    if (errorStr.includes('permission') || errorStr.includes('unauthorized')) {
      return (
        <svg className="me-2" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
          <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
        </svg>
      );
    }
    
    // Default error icon
    return (
      <svg className="me-2" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
        <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
      </svg>
    );
  };

  const getUserFriendlyMessage = (error) => {
    const errorStr = error?.message || error || '';
    
    if (errorStr.includes('Invalid PDF')) {
      return {
        title: 'Invalid PDF File',
        message: 'The file you selected is not a valid PDF. Please ensure you\'re uploading a properly formatted PDF document.',
        suggestion: 'Try opening the file in a PDF viewer to verify it\'s not corrupted, or try re-saving the file as a PDF.'
      };
    }
    
    if (errorStr.includes('Please select a PDF file only') || errorStr.includes('only accept PDF files')) {
      return {
        title: 'File Type Error',
        message: 'The system detected that your file may not be a valid PDF format.',
        suggestion: 'Please ensure your file has a .pdf extension and try re-saving it as a PDF using Adobe Acrobat or another PDF tool. Some files may appear as PDFs but have different internal formats.'
      };
    }
    
    if (errorStr.includes('timeout') || errorStr.includes('timed out')) {
      return {
        title: 'Upload Timeout',
        message: 'The upload took too long to complete. This might be due to a slow internet connection or a large file.',
        suggestion: 'Please check your internet connection and try again. Consider using a smaller file if possible.'
      };
    }
    
    if (errorStr.includes('permission') || errorStr.includes('unauthorized') || errorStr.includes('Unauthorized')) {
      return {
        title: 'Permission Denied',
        message: errorStr.includes('not listed as an instructor') 
          ? 'You are not listed as an instructor for this class in the system.'
          : 'You don\'t have permission to upload files for this class.',
        suggestion: 'Please verify you are assigned as an instructor for this specific class section. If you believe this is an error, contact your administrator with the class number and term information.'
      };
    }
    
    if (errorStr.includes('file too large') || errorStr.includes('413')) {
      return {
        title: 'File Too Large',
        message: 'The PDF file is too large to upload.',
        suggestion: 'Please compress the PDF or use a file smaller than 50MB.'
      };
    }
    
    if (errorStr.includes('network') || errorStr.includes('Network Error')) {
      return {
        title: 'Network Error',
        message: 'There was a problem connecting to the server.',
        suggestion: 'Please check your internet connection and try again.'
      };
    }
    
    if (errorStr.includes('Authentication error')) {
      return {
        title: 'Authentication Problem',
        message: 'There was an issue with your login session.',
        suggestion: 'Please try refreshing the page or logging in again.'
      };
    }
    
    // Default error message
    return {
      title: 'Upload Error',
      message: errorStr || 'An unexpected error occurred.',
      suggestion: 'Please try again. If the problem persists, contact support.'
    };
  };

  return (
    <div className="error-display">
      {errors.map((error, index) => {
        const { title, message, suggestion } = getUserFriendlyMessage(error);
        
        return (
          <div key={index} className="alert alert-danger alert-dismissible" role="alert">
            <div className="d-flex align-items-start">
              {getErrorIcon(error)}
              <div className="flex-grow-1">
                <h6 className="alert-heading mb-1">{title}</h6>
                <p className="mb-2">{message}</p>
                {suggestion && (
                  <small className="text-muted">
                    <strong>Suggestion:</strong> {suggestion}
                  </small>
                )}
                
                {showDetails && (
                  <details className="mt-2">
                    <summary className="btn btn-link p-0 small">
                      Show Technical Details
                    </summary>
                    <pre className="bg-light p-2 mt-1 small" style={{ fontSize: '0.75rem' }}>
                      {error?.stack || error?.message || error}
                    </pre>
                  </details>
                )}
              </div>
              
              <div className="d-flex gap-1 ms-2">
                {onRetry && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={handleRetry}
                    title="Try again"
                  >
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                      <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
                    </svg>
                  </button>
                )}
                
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => handleDismiss(index)}
                ></button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Development-only error monitoring component
 * Shows recent errors and system health
 */
const ErrorMonitor = () => {
  const [errors, setErrors] = useState([]);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Only show in development
    if (process.env.NODE_ENV !== 'development') {
      return;
    }

    // Listen for console errors
    const originalError = console.error;
    const originalWarn = console.warn;

    console.error = (...args) => {
      originalError.apply(console, args);
      addError('error', args.join(' '));
    };

    console.warn = (...args) => {
      originalWarn.apply(console, args);
      addError('warning', args.join(' '));
    };

    // Listen for unhandled promise rejections
    const handleUnhandledRejection = (event) => {
      addError('error', `Unhandled Promise Rejection: ${event.reason}`);
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      console.error = originalError;
      console.warn = originalWarn;
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const addError = (level, message) => {
    const error = {
      id: Date.now(),
      level,
      message,
      timestamp: new Date().toLocaleTimeString()
    };

    setErrors(prev => [error, ...prev.slice(0, 9)]); // Keep last 10 errors
  };

  const clearErrors = () => {
    setErrors([]);
    errorLogger.logUserAction('error-monitor-cleared');
  };

  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <div 
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 9999,
        backgroundColor: 'white',
        border: '1px solid #ccc',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        maxWidth: '400px'
      }}
    >
      <div 
        style={{
          padding: '8px 12px',
          backgroundColor: errors.length > 0 ? '#dc3545' : '#28a745',
          color: 'white',
          borderRadius: '8px 8px 0 0',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
        onClick={() => setIsVisible(!isVisible)}
      >
        <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
          🔍 Error Monitor ({errors.length})
        </span>
        <span style={{ fontSize: '12px' }}>
          {isVisible ? '▼' : '▲'}
        </span>
      </div>

      {isVisible && (
        <div style={{ padding: '12px', maxHeight: '300px', overflowY: 'auto' }}>
          <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
            <small style={{ color: '#666' }}>Recent Errors & Warnings</small>
            {errors.length > 0 && (
              <button 
                onClick={clearErrors}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#007bff',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Clear
              </button>
            )}
          </div>

          {errors.length === 0 ? (
            <div style={{ color: '#28a745', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
              ✅ No recent errors
            </div>
          ) : (
            <div>
              {errors.map(error => (
                <div 
                  key={error.id}
                  style={{
                    padding: '6px 8px',
                    marginBottom: '4px',
                    backgroundColor: error.level === 'error' ? '#fff5f5' : '#fffbf0',
                    border: `1px solid ${error.level === 'error' ? '#fed7d7' : '#feebc8'}`,
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                    <span style={{ 
                      color: error.level === 'error' ? '#c53030' : '#d69e2e',
                      fontWeight: 'bold'
                    }}>
                      {error.level === 'error' ? '❌' : '⚠️'} {error.level.toUpperCase()}
                    </span>
                    <span style={{ color: '#666', fontSize: '10px' }}>
                      {error.timestamp}
                    </span>
                  </div>
                  <div style={{ color: '#333', wordBreak: 'break-word' }}>
                    {error.message.length > 100 
                      ? error.message.substring(0, 100) + '...'
                      : error.message
                    }
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: '8px', fontSize: '10px', color: '#666', textAlign: 'center' }}>
            Development Mode Only
          </div>
        </div>
      )}
    </div>
  );
};

export { ErrorDisplay, ErrorMonitor };